import { api } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';

export const editorKeys = {
  config: (fileId, mode) => ['editor-config', fileId, mode ?? 'edit'],
};

/** Public URL of the ONLYOFFICE Document Server (loads api.js in the browser). */
export const ONLYOFFICE_URL = (import.meta.env.VITE_ONLYOFFICE_URL || '').replace(/\/+$/, '');

/** True when the integration is configured on the frontend. */
export const ONLYOFFICE_ENABLED = Boolean(ONLYOFFICE_URL);

/** Extensions the editor can open (view) — Office + PDF. */
const VIEWABLE_EXT = new Set([
  'docx',
  'doc',
  'odt',
  'rtf',
  'txt',
  'xlsx',
  'xls',
  'ods',
  'csv',
  'pptx',
  'ppt',
  'odp',
  'pdf',
]);

/** Extensions the editor can round-trip (edit + save). */
const EDITABLE_EXT = new Set(['docx', 'xlsx', 'pptx']);

/** Office formats the backend can convert to PDF for "share as PDF" (pdf itself excluded). */
const PDF_CONVERTIBLE_EXT = new Set([
  'docx',
  'doc',
  'odt',
  'rtf',
  'txt',
  'xlsx',
  'xls',
  'ods',
  'csv',
  'pptx',
  'ppt',
  'odp',
]);

function extOf(name = '') {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Can this file be shared as a PDF conversion? (Needs the ONLYOFFICE integration.) */
export function canShareAsPdf(file) {
  return ONLYOFFICE_ENABLED && PDF_CONVERTIBLE_EXT.has(extOf(file?.name));
}

/** Can this file be opened in the ONLYOFFICE editor at all? */
export function canOpenInEditor(file) {
  return ONLYOFFICE_ENABLED && VIEWABLE_EXT.has(extOf(file?.name));
}

/** Is this file editable (vs view-only) in ONLYOFFICE? */
export function isEditableInEditor(file) {
  return ONLYOFFICE_ENABLED && EDITABLE_EXT.has(extOf(file?.name));
}

// Mirrors kindOf() in files/components/InlinePreview.jsx — the mime branches
// the file viewer can render natively. Keep in sync.
function hasNativePreview(mime = '') {
  return (
    mime.startsWith('image/') ||
    mime === 'application/pdf' ||
    mime.startsWith('text/') ||
    mime === 'application/json'
  );
}

/**
 * Office docs the viewer can't render — these open straight into the editor
 * (Drive-style). Natively previewable types (pdf/text/images) keep the viewer.
 */
export function opensInEditorByDefault(file) {
  return canOpenInEditor(file) && !hasNativePreview(file?.mimeType);
}

/** Route a file opens at: editor for office docs (when configured), viewer otherwise. */
export function fileOpenPath(file) {
  if (!opensInEditorByDefault(file)) return `/files/${file.id}`;
  // View-only types state the intent up front instead of relying on the
  // backend's edit→view downgrade.
  return isEditableInEditor(file)
    ? `/files/${file.id}/edit`
    : `/files/${file.id}/edit?mode=view`;
}

function ws() {
  const workspaceId = useAuthStore.getState().workspaceId;
  if (!workspaceId) throw new Error('No active workspace selected');
  return `/workspaces/${workspaceId}`;
}

/**
 * Fetch a signed DocEditor config for a file. `mode` is a request; the backend
 * downgrades to view when the caller lacks write access or the type is not
 * round-trippable. Returns { documentServerUrl, fileName, canEdit, config }.
 */
export async function getEditorConfig(fileId, mode = 'edit') {
  const { data } = await api.get(`${ws()}/files/${fileId}/editor-config`, { params: { mode } });
  return data;
}
